import * as React from "react";
import { Button } from "antd";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useTableKindId } from "../../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../../newcomponents/table/data/useTableItemQueryData";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { CardContent } from "../../../components/antd/card/CardContent";
import { Table } from "../../../newcomponents/table/table";
import { NameDescriptionColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TableModal } from "../../../components/antd/TableModal";
import { TableHeaderRefreshButton } from "../../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionNoLinkColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { Domain } from "../../../schema/types/domain/entity";
import clsx from "clsx";
import { DomainTableItem, getDomainTableItem } from "../../../mst/kinds/domain.helpers";
import { PolicyInput } from "../../../schema/types/policy/input";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../gvc/testNewForm/utils";
import { store } from "../../../store";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { KindSelectState } from "../../../components/antd/kindselect/KindSelect";

interface Props {}
export const PolicyTargetDomain: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const { kind: addTableKind, id: addTableId } = useTableKindId("domain", "policy-target-items-domain");
  const qData = useTableItemQueryData<Domain>(addTableKind, {});

  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<DomainTableItem[]>([]);
  const [itemCache, setItemCache] = React.useState<Record<string, DomainTableItem>>({});
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  const domains = useCpFormValue(`targets`);
  const domainTableItems: DomainTableItem[] = React.useMemo(() => domains.map((d) => getDomainTableItem(d)), [domains]);

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: PolicyInput = form.values as any;

    const currentItems: KindSelectState[] = cloneDeep(values.targets);
    currentItems.splice(index, 1);
    formSetValue(id, `targets`, currentItems);

    formTriggerValidation(id);
  };

  function onOkAddModal() {
    const form = store.getState().form.instances[id];
    const values: PolicyInput = form.values as any;

    const currentItems: KindSelectState[] = cloneDeep(values.targets);

    // TODO selectionsToAdd can be renamed to something with link
    for (const selectionToAdd of selectionsToAdd) {
      const currentLinks = currentItems.map((u) => u.link);
      const item = selectionItems.find((i) => i.selfLink === selectionToAdd);
      if (item) {
        itemCache[selectionToAdd] = item;
      }

      if (currentLinks.includes(selectionToAdd)) {
        continue;
      }

      const { name } = parseLink(selectionToAdd, { useInputCtx: true });
      currentItems.push({
        value: name,
        item: item,
        link: selectionToAdd,
      });
    }
    // TODO was sorting the links but no more because now it is kindselectstate

    // TODO initialize this item cache on load for detail page
    setItemCache(itemCache);

    formSetValue(id, `targets`, currentItems);

    setSelectionsToAdd([]);
    setSelectionItems([]);
    setIsAddingItem(false);

    formTriggerValidation(id);
  }

  const currentLinks = domains.map((t) => t.link);
  const addDomainTableItems: DomainTableItem[] = qData.visibleItems
    .map((item) => getDomainTableItem({ item: item }))
    .filter((item) => !currentLinks.includes(item.selfLink));

  return (
    <>
      <CardContent>
        {domainTableItems.length > 0 ? (
          <Table<DomainTableItem>
            tableId="policy-target-items-domain-editable"
            data={domainTableItems}
            hideSettings
            columns={[
              NameDescriptionNoLinkColumn({ enableSort: false }),
              {
                id: "domainStatusText",
                label: "Status",
                cell: ({ getValue }) => {
                  const value = getValue();
                  return (
                    <span
                      className={clsx("truncate pr-2", {
                        "color-action": value === "Ready",
                        "color-warning": value === "Warning",
                      })}
                    >
                      {value}
                    </span>
                  );
                },
              },
              { id: "type", label: "Type" },
              { id: "mappedGVC", label: "GVC" },
              { id: "endpointCount", label: "Endpoints" },
              TagsColumn(),
              CreatedColumn(),
              LastModifiedColumn(),
              {
                id: "action",
                label: "",
                enableResize: false,
                size: 120,
                cell: ({ row }) => {
                  return (
                    <div className="w-full flex justify-end">
                      <Button
                        htmlType={`button`}
                        type={"text"}
                        icon={<CloseOutlined />}
                        danger
                        onClick={() => onRemoveItem(row.index)}
                      />
                    </div>
                  );
                },
              },
            ]}
          />
        ) : null}
        <TableModal
          open={isAddingItem}
          onCancel={() => setIsAddingItem(false)}
          onOk={() => {
            onOkAddModal();
          }}
        >
          <Table<DomainTableItem>
            tableId={addTableId}
            title={"Select Domains to Add"}
            selectMode="multi"
            selectKey="selfLink"
            selections={selectionsToAdd}
            onSelectionsChange={(keys, items) => {
              setSelectionsToAdd(keys);
              setSelectionItems(items!);
            }}
            data={addDomainTableItems}
            headerRenderer={() => {
              return (
                <>
                  <TableHeaderRefreshButton onClick={qData.fetchItems} />
                  <CustomTableCPLNQuery
                    tableId={addTableId}
                    kind={addTableKind}
                    onQuery={qData.setQuery}
                    isLoading={qData.isLoadingQueried}
                  />
                </>
              );
            }}
            enableFilter
            isLoading={qData.isLoading}
            onSortChange={qData.setSort}
            onSearchChange={qData.setSearch}
            isLoadingMore={qData.isLoadingMore}
            hasMore={qData.hasMore}
            onLoadMore={qData.fetchNextPage}
            columns={[
              NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
              {
                id: "domainStatusText",
                label: "Status",
                cell: ({ getValue }) => {
                  const value = getValue();
                  return (
                    <span
                      className={clsx("truncate pr-2", {
                        "color-action": value === "Ready",
                        "color-warning": value === "Warning",
                      })}
                    >
                      {value}
                    </span>
                  );
                },
              },
              { id: "type", label: "Type" },
              { id: "mappedGVC", label: "GVC" },
              { id: "endpointCount", label: "Endpoints" },
              TagsColumn(),
              CreatedColumn(),
              LastModifiedColumn(),
            ]}
          />
        </TableModal>
        <Button
          htmlType={`button`}
          className="mt-4"
          type={"dashed"}
          icon={<PlusCircleOutlined />}
          onClick={() => setIsAddingItem(true)}
        >
          Add Domain
        </Button>
      </CardContent>
    </>
  );
};
